import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import {
  getApiV1ClassroomdisciplineClassModulesIdQueryKey,
  type SaveClassModuleViewModel,
  usePostApiV1ClassroomdisciplineClassModules,
  usePutApiV1ClassroomdisciplineClassModulesId,
} from '@/shared/api/generated';

/** Opções de callback para a persistência do módulo. */
export interface SaveClassModuleOptions {
  readonly onSuccess?: () => void;
  readonly onError?: () => void;
}

/**
 * Fronteira de escrita do módulo. Seleciona `POST` (cadastro) ou `PUT` (edição)
 * conforme `moduleId`, e invalida o detalhe no sucesso da edição. Encapsula os
 * hooks de mutação gerados pelo Kubb, expondo um `save` unificado.
 *
 * @param moduleId - `id` do módulo em edição; `undefined` no cadastro.
 * @returns `{ save, isPending, isError, error }`.
 */
export function useSaveClassModule(moduleId?: number) {
  const queryClient = useQueryClient();
  const isEdit = moduleId !== undefined;

  const createMutation = usePostApiV1ClassroomdisciplineClassModules();
  const updateMutation = usePutApiV1ClassroomdisciplineClassModulesId();
  const active = isEdit ? updateMutation : createMutation;

  const save = useCallback(
    (data: SaveClassModuleViewModel, options?: SaveClassModuleOptions) => {
      const onSuccess = () => {
        if (isEdit) {
          void queryClient.invalidateQueries({
            queryKey: getApiV1ClassroomdisciplineClassModulesIdQueryKey(moduleId),
          });
        }
        options?.onSuccess?.();
      };

      const handlers = { onSuccess, onError: () => options?.onError?.() };
      if (isEdit) {
        updateMutation.mutate({ id: moduleId, data }, handlers);
      } else {
        createMutation.mutate({ data }, handlers);
      }
    },
    [isEdit, moduleId, queryClient, createMutation, updateMutation],
  );

  return {
    save,
    isPending: active.isPending,
    isError: active.isError,
    error: active.error,
  } as const;
}
